import {
  cn,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@beweegbaas/ui";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { Fragment, useState } from "react";
import { useTableNavigation } from "#/hooks/use-table-navigation";
import {
  RedistributeLesgroepenDialog,
  type RedistributePrompt,
} from "#/modules/les.docent-gestuurd/components/dialog.redistribute-lesgroepen";
import { AanwezigheidField } from "#/modules/les.docent-gestuurd/components/field.aanwezigheid";
import { GedragField } from "#/modules/les.docent-gestuurd/components/field.gedrag";
import { GymkledingField } from "#/modules/les.docent-gestuurd/components/field.gymkleding";
import { LesadministratieOpmerkingenField } from "#/modules/les.docent-gestuurd/components/field.lesadministratie-opmerkingen";
import { getTeacherLesadministraties } from "#/modules/les.docent-gestuurd/lesson-teacher.functions";
import { useDocentLes } from "#/modules/les.docent-gestuurd/use-docent-les";
import { baseColorVariants, getGroupColor } from "#/modules/les.shared/group-colors";
import { getInnerLeerjaarNumberTranslation } from "#/modules/util.i18n/enum-mapping";

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/lesson/$lesson/teacher/administration",
)({
  loader: ({ params }) => getTeacherLesadministraties({ data: { lesId: params.lesson } }),
  component: TeacherLessonAdministration,
});

function TeacherLessonAdministration() {
  const { lesadministraties } = Route.useLoaderData();
  const { locale, schoolyear, school, lesson } = Route.useParams();
  const { lesson: les } = useDocentLes();
  const canRedistribute = les.status === "Actief" && les.aantalGroepen > 1;
  // Lives here, not in the row: the row remounts when its leerling moves to another groepje.
  const [redistributePrompt, setRedistributePrompt] = useState<RedistributePrompt | null>(null);

  // Enable table navigation for all focusable form elements
  useTableNavigation();

  return (
    <div className="mx-auto w-full max-w-(--container-max-width)">
      {/* Small size (h-10/px-2 cells) + container-gutter edge cells, inlined from the legacy table variants. */}
      <Table
        aria-label={m["les.administratie.student_administration"]()}
        className="[&_td]:h-10 [&_td]:px-2 [&_th]:px-2 [&_tr>*:first-child]:pl-(--container-gutter)! [&_tr>*:last-child]:pr-(--container-gutter)!"
      >
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="w-10 text-nowrap" />
            <TableHead className="pl-0!">{m["common.fields.student"]()}</TableHead>
            <TableHead className="text-nowrap">{m["les.administratie.presence"]()}</TableHead>
            <TableHead className="text-nowrap">{m["common.labels.gymkleding"]()}</TableHead>
            <TableHead className="text-nowrap">{m["les.administratie.behavior"]()}</TableHead>
            <TableHead className="text-nowrap">{m["les.administratie.remarks"]()}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {lesadministraties?.groupedLesadministraties.map((groep, index) => {
            return (
              <Fragment key={groep.groep}>
                {index > 0 && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell className="h-10 w-10" colSpan={6}></TableCell>
                  </TableRow>
                )}
                {groep.lesadministraties.map((lesadministratie) => {
                  return (
                    <TableRow key={lesadministratie.leerlingId}>
                      <TableCell data-table-sticky-left="" className="sticky left-0 z-10 w-10">
                        <span
                          className={cn(
                            `inline-flex size-5 items-center justify-center rounded
                                font-bold`,
                            baseColorVariants({
                              color: getGroupColor(groep.groep),
                              textColor: "dark",
                            }),
                          )}
                        >
                          {groep.groep === -1 ? <IconTablerX className="size-4" /> : groep.groep}
                        </span>
                      </TableCell>
                      <TableCell
                        data-table-sticky-left="edge"
                        className="sticky left-[calc(var(--container-gutter)+2rem)] z-10 pl-0!"
                      >
                        <Link
                          className="text-nowrap"
                          variant="secondary"
                          underline={true}
                          to="/$locale/{-$schoolyear}/$school/students/$id/dashboard"
                          params={{ locale, schoolyear, school, id: lesadministratie.leerlingId }}
                        >
                          {lesadministratie.leerlingNaam}
                        </Link>
                        {/* (klasnaam - leerjaar): klasnaam only when the les spans multiple
                            klassen, leerjaar only when the klassen combine leerjaren. */}
                        {((lesadministratie.showKlas && lesadministratie.klasNaam) ||
                          lesadministratie.showLeerlingLeerjaar) && (
                          <span className="text-muted-foreground ml-1">
                            {`(${[
                              lesadministratie.showKlas ? lesadministratie.klasNaam : null,
                              lesadministratie.showLeerlingLeerjaar
                                ? getInnerLeerjaarNumberTranslation(
                                    lesadministratie.leerlingLeerjaar,
                                  )
                                : null,
                            ]
                              .filter(Boolean)
                              .join(" - ")})`}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="w-[20%]">
                        <AanwezigheidField
                          lesId={lesson}
                          leerlingId={lesadministratie.leerlingId}
                          value={lesadministratie.aanwezigheid}
                          onGroepjesChanged={
                            canRedistribute
                              ? (isAanwezig) =>
                                  setRedistributePrompt({
                                    leerlingNaam: lesadministratie.leerlingNaam,
                                    isAanwezig,
                                  })
                              : undefined
                          }
                        />
                      </TableCell>
                      <TableCell className="w-[20%]">
                        <GymkledingField
                          lesId={lesson}
                          leerlingId={lesadministratie.leerlingId}
                          value={lesadministratie.gymkleding}
                        />
                      </TableCell>
                      <TableCell className="w-[20%]">
                        <GedragField
                          lesId={lesson}
                          leerlingId={lesadministratie.leerlingId}
                          value={lesadministratie.timeoutType}
                        />
                      </TableCell>
                      <TableCell className="w-0 text-nowrap">
                        <LesadministratieOpmerkingenField
                          lesId={lesson}
                          leerlingId={lesadministratie.leerlingId}
                          value={lesadministratie.opmerking}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
      <RedistributeLesgroepenDialog
        lesId={lesson}
        prompt={redistributePrompt}
        onClose={() => setRedistributePrompt(null)}
      />
      <Outlet />
    </div>
  );
}
